/**
 * HP-triggered intermissions (`Phase.intermission`). The AI enters the phase as usual (timers
 * restart from `first`); this module leaps the boss to its mark, keeps the boss aura up, watches
 * the end conditions and the timeout, and tells the AI when to move on to the next phase.
 */
import type { EffectCtx, EncounterDef, EncounterState, IntermissionDef, SimContext, Unit, Vec2 } from '../../contracts';
import { TICK_MS } from '../../contracts';
import { arenaOf, dist, hasAuraOn, markersOf, mstate, resolveKeyScaled, runEffects, unitOf } from './state';

function bossesOf(ctx: SimContext, enc: EncounterState): Unit[] {
  const out: Unit[] = [];
  for (const id of enc.bossIds) {
    const u = ctx.world.units[id];
    if (u?.alive) out.push(u);
  }
  return out;
}

function landingPoint(ctx: SimContext, def: IntermissionDef, boss: Unit): Vec2 | null {
  const mt = def.moveTo;
  if (!mt) return null;
  if (mt === 'arenaCenter') {
    const a = arenaOf(ctx, boss);
    return a ? { x: a.x + a.w / 2, y: a.y + a.h / 2 } : null;
  }
  if ('marker' in mt) {
    const marks = markersOf(ctx, boss, mt.marker);
    if (marks.length === 0) return null;
    if (mt.pick === 'first') return marks[0]!;
    const sign = mt.pick === 'farthest' ? -1 : 1;
    let best = marks[0]!;
    for (const m of marks) if (sign * (dist(m, boss.pos) - dist(best, boss.pos)) < 0) best = m;
    return best;
  }
  return mt;
}

const bossCtx = (boss: Unit, abilityId: string, point: Vec2 | null = null): EffectCtx => ({
  sourceId: boss.id,
  abilityId,
  targetId: boss.id,
  point: point ?? { x: boss.pos.x, y: boss.pos.y },
  depth: 1,
});

/** The AI entered phase `index`, which has an intermission. */
export function startIntermission(ctx: SimContext, enc: EncounterState, encDef: EncounterDef, index: number): void {
  const def = encDef.phases[index]?.intermission;
  if (!def) return;
  const st = mstate(ctx.world);
  const bosses = bossesOf(ctx, enc);
  const lead = bosses[0];
  const landing = lead ? landingPoint(ctx, def, lead) : null;
  const leapMs = def.leapMs ?? 0;
  for (const b of bosses) {
    if (b.cast) ctx.sys.casting.cancelCast(ctx, b.id, 'cc');
    if (landing) {
      const to = ctx.sys.spatial.clampToWalkable(ctx.world, landing);
      if (leapMs <= 0) {
        b.pos = to;
        b.move = null;
      } else b.move = { mode: 'forced', to, speed: dist(b.pos, to) / (leapMs / 1000), until: ctx.now + leapMs };
    }
    if (def.bossAura) ctx.sys.combat.applyAura(ctx, b.id, def.bossAura.id, b.id, encDef.id);
  }
  st.intermission = {
    encounterId: enc.encounterId,
    phaseIndex: index,
    startedAt: ctx.now,
    endsAt: ctx.now + Math.max(TICK_MS, resolveKeyScaled(ctx, def.maxMs, 12000)),
    landingAt: landing ? ctx.now + leapMs : null,
    landing,
    sawAdds: false,
    casts: [],
    seenAuras: [],
  };
}

function addsOf(ctx: SimContext, enc: EncounterState, def: IntermissionDef): Unit[] {
  const out: Unit[] = [];
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive || u.kind !== 'npc' || enc.bossIds.includes(u.id)) continue;
    if (def.addNpcIds ? def.addNpcIds.includes(u.npcId ?? '') : !!u.summonerId && enc.bossIds.includes(u.summonerId)) out.push(u);
  }
  return out;
}

function finish(ctx: SimContext, enc: EncounterState, def: IntermissionDef, timedOut: boolean): void {
  const bosses = bossesOf(ctx, enc);
  const lead = bosses[0];
  if (def.bossAura) for (const b of bosses) ctx.sys.combat.removeAura(ctx, b.id, { auraId: def.bossAura.id }, 'expired');
  if (lead) {
    if (timedOut) {
      runEffects(ctx, def.onTimeout, bossCtx(lead, enc.encounterId));
      for (const add of addsOf(ctx, enc, def)) {
        runEffects(ctx, def.perSurvivingAdd, { ...bossCtx(lead, enc.encounterId), targetId: add.id });
        ctx.ops.despawn(add.id);
      }
    } else runEffects(ctx, def.onEnd, bossCtx(lead, enc.encounterId));
  }
  mstate(ctx.world).intermission = null;
}

/**
 * Per encounter update while the current phase is an intermission. Returns true when it is over
 * (the AI then enters the next phase).
 */
export function updateIntermission(ctx: SimContext, enc: EncounterState, encDef: EncounterDef): boolean {
  const def = encDef.phases[enc.phaseIndex]?.intermission;
  if (!def) return false;
  const st = mstate(ctx.world);
  let it = st.intermission;
  if (!it || it.encounterId !== enc.encounterId || it.phaseIndex !== enc.phaseIndex) {
    startIntermission(ctx, enc, encDef, enc.phaseIndex);
    it = st.intermission!;
  }
  const bosses = bossesOf(ctx, enc);
  const lead = bosses[0];
  if (!lead) return false;
  if (it.landingAt !== null && ctx.now >= it.landingAt) {
    it.landingAt = null;
    runEffects(ctx, def.landing, bossCtx(lead, enc.encounterId, it.landing));
  }
  // Watch boss casts for interrupted / castCompleted ends.
  let ended = false;
  for (const c of [...it.casts]) {
    const b = unitOf(ctx, c.unitId);
    if (b?.cast?.abilityId === c.abilityId) {
      c.endsAt = b.cast.endsAt;
      continue;
    }
    it.casts = it.casts.filter((x) => x !== c);
    const completed = ctx.now >= c.endsAt;
    for (const e of def.endsWhen ?? []) {
      if (typeof e === 'object' && 'interrupted' in e && e.interrupted === c.abilityId && !completed) ended = true;
      if (typeof e === 'object' && 'castCompleted' in e && e.castCompleted === c.abilityId && completed) ended = true;
    }
  }
  for (const b of bosses) {
    if (b.cast && !it.casts.some((c) => c.unitId === b.id && c.abilityId === b.cast!.abilityId)) {
      const watched = (def.endsWhen ?? []).some(
        (e) => typeof e === 'object' && (('interrupted' in e && e.interrupted === b.cast!.abilityId) || ('castCompleted' in e && e.castCompleted === b.cast!.abilityId)),
      );
      if (watched) it.casts.push({ unitId: b.id, abilityId: b.cast.abilityId, endsAt: b.cast.endsAt });
    }
  }
  for (const e of def.endsWhen ?? []) {
    if (e === 'addsDead') {
      const n = addsOf(ctx, enc, def).length;
      if (n > 0) it.sawAdds = true;
      else if (it.sawAdds) ended = true;
    } else if ('auraGone' in e) {
      const has = bosses.some((b) => hasAuraOn(b, e.auraGone));
      if (has && !it.seenAuras.includes(e.auraGone)) it.seenAuras.push(e.auraGone);
      else if (!has && it.seenAuras.includes(e.auraGone)) ended = true;
    }
  }
  if (ended) {
    finish(ctx, enc, def, false);
    return true;
  }
  if (ctx.now >= it.endsAt) {
    finish(ctx, enc, def, true);
    return true;
  }
  return false;
}

/** Drop a stale intermission (encounter ended / wiped). Called from the mechanics tick. */
export function cleanupIntermission(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  const it = st?.intermission;
  if (!st || !it) return;
  const enc = ctx.world.encounter;
  if (enc && enc.encounterId === it.encounterId && enc.result === null) return;
  const def = ctx.content.encounter(it.encounterId)?.phases[it.phaseIndex]?.intermission;
  if (def?.bossAura) {
    for (const id of ctx.world.unitOrder) {
      const u = ctx.world.units[id];
      if (u?.encounterId === it.encounterId && hasAuraOn(u, def.bossAura.id)) ctx.sys.combat.removeAura(ctx, u.id, { auraId: def.bossAura.id }, 'expired');
    }
  }
  st.intermission = null;
}
